/**
 * Embarque de importación (detalle) — cabecera + chasis + pagos + documentos
 * + liquidación.
 *
 * El embarque es el GRUPO (un contenedor/orden al suplidor); los chasis son
 * líneas que llegan del packing list (Excel parseado aquí, el backend recibe
 * filas JSON) y los desembolsos su calendario de pagos multimoneda. Pipeline
 * de la cabecera: negociacion → fabricacion → transito → puerto →
 * liquidacion → cerrado (+ cancelado). El % de anticipo es negociado por
 * embarque (nunca 30% fijo) y el saldo se paga 15-20 días antes del ETA.
 *
 * La LIQUIDACIÓN BLOQUEA: prorratea el total desembolsado en RD$ base por el
 * peso FOB de cada línea y exige precio de venta y mínimo por chasis; sin
 * eso no hay entrada a inventario. La recepción crea los chasis en producto
 * (misma vía que el registro de chasis) y cierra el embarque.
 *
 * Se usa embebida en un Dialog fullScreen desde embarques.js.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid } from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx, ConfirmarDialog } from "components/financepro";
import { puedeAccionPrograma, ocultaCostos } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import { useMarcasEstandar, useVistaPrevia } from "lib/marcasEstandar";
import AvisoMarcasEstandar, { VistaPreviaMarca } from "lib/AvisoMarcasEstandar";
import { fmtMoney, toNum } from "lib/moneda";
import { parseExcelPreview, downloadTemplateXlsx } from "modules/importacion/excelPreview";

export const ESTADO_CHIP = {
  negociacion: { label: "Negociación", bg: "#ECEFF1", fg: "#455A64" },
  fabricacion: { label: "Fabricación", bg: "#FFF3E0", fg: "#E65100" },
  transito: { label: "En tránsito", bg: "#E3F2FD", fg: "#1565C0" },
  puerto: { label: "En puerto", bg: "#EDE7F6", fg: "#5E35B1" },
  liquidacion: { label: "Liquidado", bg: "#E0F2F1", fg: "#00695C" },
  cerrado: { label: "Cerrado", bg: "#E8F5E9", fg: "#2E7D32" },
  cancelado: { label: "Cancelado", bg: "#FFEBEE", fg: "#C62828" },
};

const LINEA_CHIP = {
  pendiente: { label: "Pendiente", bg: "#FFF3E0", fg: "#E65100" },
  recibido: { label: "Recibido", bg: "#E8F5E9", fg: "#2E7D32" },
};

export const chipEstado = (estado, mapa = ESTADO_CHIP) => {
  const c = mapa[estado] || { label: estado, bg: "#ECEFF1", fg: "#455A64" };
  return <Chip size="small" label={c.label} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700 }} />;
};

/** Orden del pipeline; cerrado/cancelado no se ponen a mano. */
const ESTADOS_VIVOS = ["negociacion", "fabricacion", "transito", "puerto", "liquidacion"];

export const TIPOS_PAGO = [
  { value: "anticipo", label: "Anticipo al suplidor" },
  { value: "saldo", label: "Saldo al suplidor" },
  { value: "flete", label: "Flete y cargos de naviera" },
  { value: "despacho", label: "Despacho portuario" },
  { value: "transporte", label: "Transportista" },
  { value: "aduanero", label: "Agente aduanero" },
  { value: "otro", label: "Otro" },
];

const TIPOS_DOCUMENTO = [
  { value: "proforma", label: "Proforma" },
  { value: "facturacomercial", label: "Factura comercial" },
  { value: "bl", label: "BL" },
  { value: "packinglist", label: "Packing list" },
  { value: "otro", label: "Otro" },
];

/** 'dd/mm/aaaa' (respuesta del server) → 'aaaa-mm-dd' (input date). */
const aInputDate = (v) => {
  const p = String(v || "").split("/");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : "";
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

const filtrarSuplidores = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.id} ${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

/**
 * Mapeo laxo de encabezados del packing list: cada suplidor manda el Excel
 * como quiere, así que se buscan sinónimos por columna. Solo el chasis es
 * obligatorio.
 */
const CAMPOS_PACKING = {
  chasis: ["chasis", "vin", "chassis", "no chasis", "no. chasis", "serie", "frame", "frame no"],
  marca: ["marca", "brand", "make"],
  modelo: ["modelo", "model"],
  color: ["color", "colore", "colour"],
  ano: ["ano", "año", "anio", "year"],
  cilindraje: ["cilindraje", "cilindrada", "cc", "displacement"],
  maquina: ["maquina", "máquina", "motor", "engine", "engine no", "no motor"],
  costofob: ["costo fob", "fob", "costo", "precio fob", "unit price", "precio unitario", "price"],
};

const normalizarEncabezado = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.]/g, "")
    .replace(/\s+/g, " ");

function mapearEncabezados(headers) {
  const mapa = {};
  Object.entries(CAMPOS_PACKING).forEach(([campo, sinonimos]) => {
    const norm = sinonimos.map(normalizarEncabezado);
    const encontrado = headers.find((h) => norm.includes(normalizarEncabezado(h)));
    if (encontrado) mapa[campo] = encontrado;
  });
  return mapa;
}

/** Descarga un binario base64 con su mime real (BL en PDF, fotos, etc.). */
function descargarB64(b64, nombre, mime) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: mime || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre || "documento";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

const montoEnBase = (monto, moneda, tasa) =>
  String(moneda || "P").toUpperCase() === "U" ? toNum(monto) * toNum(tasa) : toNum(monto);

export default function Embarque({ id, onExit, onChanged }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  const puedeModificar = puedeAccionPrograma(roleDetalles, "importaciones", "modificar");
  const puedeBorrar = puedeAccionPrograma(roleDetalles, "importaciones", "borrar");
  const sinCostos = ocultaCostos(roleDetalles);

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [embarque, setEmbarque] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState(0);

  const [suplidores, setSuplidores] = useState([]);
  const [navieras, setNavieras] = useState([]);
  const [transportistas, setTransportistas] = useState([]);

  // Diálogos
  const [confirmEstado, setConfirmEstado] = useState(null);
  const [confirmCancelar, setConfirmCancelar] = useState(false);
  const [confirmRecibir, setConfirmRecibir] = useState(false);
  const [dialogLinea, setDialogLinea] = useState(null);
  // Switch "Estandarizar marcas y modelos": vista previa de la escritura
  // estándar en el diálogo de línea y aviso en el packing list.
  const estandar = useMarcasEstandar();
  const vistaLinea = useVistaPrevia(
    estandar.resolver,
    estandar.activo && dialogLinea != null,
    dialogLinea?.marca,
    dialogLinea?.modelo
  );
  const [dialogPago, setDialogPago] = useState(null);
  const [quitarLinea, setQuitarLinea] = useState(null);
  const [quitarPago, setQuitarPago] = useState(null);
  const [quitarDoc, setQuitarDoc] = useState(null);
  const [dialogDoc, setDialogDoc] = useState(false);
  const [docForm, setDocForm] = useState({ tipo: "packinglist", nombre: "", mime: "", b64: "" });
  const [packing, setPacking] = useState(null);

  // Precios de la liquidación (id de línea -> {precio, mprecio})
  const [precios, setPrecios] = useState({});

  // Requisitos de la siguiente fase (los calcula el backend: _requisitos_avance)
  const [requisitos, setRequisitos] = useState(null);
  const [multiempresa, setMultiempresa] = useState({ activo: false, empresas: [] });
  const [empresaRecibir, setEmpresaRecibir] = useState(1);
  // La cabecera se reconstruye desde el servidor solo si el usuario no la
  // ha tocado (o tras guardarla): registrar un pago no borra lo escrito.
  const formSucio = useRef(false);
  const tabsRef = useRef(null); // contenedor de pestañas, para el salto a Liquidación
  const editarForm = (fn) => {
    formSucio.current = true;
    setForm(fn);
  };

  const inputPacking = useRef(null);

  const vivo = embarque && ESTADOS_VIVOS.includes(embarque.estado);
  const liquidado = Boolean(embarque?.fechaliquidacion);
  const puedeRecibir = liquidado && embarque?.estado === "liquidacion";

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/importaciones_datos", { id });
      if (ok && data.embarque) {
        setEmbarque(data.embarque);
        setLineas(data.lineas || []);
        setPagos(data.pagos || []);
        setDocumentos(data.documentos || []);
        setRequisitos(data.requisitos || null);
        setMultiempresa({
          activo: Boolean(data.multiempresa),
          empresas: data.empresas || [],
        });
        const e = data.embarque;
        if (!formSucio.current)
          setForm({
            suplidor_id: e.suplidor_id,
            suplidornombre: e.suplidornombre,
            proforma: e.proforma,
            moneda: e.moneda,
            tasa: e.tasa || "",
            montofob: e.montofob ?? "",
            porcentajeanticipo: e.porcentajeanticipo ?? "",
            diasfabricacion: e.diasfabricacion,
            naviera: e.naviera,
            transportista: e.transportista,
            bl: e.bl,
            contenedor: e.contenedor,
            fechaproforma: aInputDate(e.fechaproforma),
            fechaanticipo: aInputDate(e.fechaanticipo),
            fechaembarque: aInputDate(e.fechaembarque),
            fechaeta: aInputDate(e.fechaeta),
            fechallegada: aInputDate(e.fechallegada),
            fechamanifestacion: aInputDate(e.fechamanifestacion),
            notas: e.notas,
          });
        setPrecios((prev) => {
          const s = { ...prev };
          (data.lineas || []).forEach((l) => {
            if (!s[l.id]) s[l.id] = { precio: l.precio ?? "", mprecio: l.mprecio ?? "" };
          });
          return s;
        });
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el embarque", color: "error" });
      }
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    let activo = true;
    Promise.all([
      post("/api/cxp_suplidores", {}),
      post("/api/importaciones_catalogo_listar", { tipo: "naviera" }),
      post("/api/importaciones_catalogo_listar", { tipo: "transportista" }),
    ]).then(([supl, nav, tra]) => {
      if (!activo) return;
      if (supl.ok) setSuplidores(supl.data.suplidores || []);
      if (nav.ok) setNavieras((nav.data.catalogo || []).map((c) => c.nombre));
      if (tra.ok) setTransportistas((tra.data.catalogo || []).map((c) => c.nombre));
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambio = () => {
    cargar();
    if (onChanged) onChanged();
  };

  const ejecutar = async (endpoint, body, exito) => {
    setGuardando(true);
    try {
      const { ok, data } = await post(endpoint, body);
      if (ok) {
        showAlert({ message: data.mensajeok || exito, color: "success" });
        cambio();
        return true;
      }
      showAlert({ message: data.Error || "Error al guardar", color: "error" });
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const guardarCabecera = () => {
    // Solo se mandan los campos de las secciones habilitadas: el backend
    // rechaza los de fases posteriores.
    const idx = ESTADOS_VIVOS.indexOf(embarque?.estado);
    const body = {
      id,
      suplidor_id: form.suplidor_id,
      suplidornombre: form.suplidornombre,
      proforma: form.proforma,
      moneda: form.moneda,
      tasa: toNum(form.tasa),
      montofob: form.montofob,
      porcentajeanticipo: form.porcentajeanticipo,
      diasfabricacion: form.diasfabricacion,
      fechaproforma: form.fechaproforma,
      fechaanticipo: form.fechaanticipo,
      notas: form.notas,
    };
    if (idx >= 1) {
      Object.assign(body, {
        naviera: form.naviera,
        bl: form.bl,
        contenedor: form.contenedor,
        fechaembarque: form.fechaembarque,
        fechaeta: form.fechaeta,
        fechamanifestacion: form.fechamanifestacion,
      });
    }
    if (idx >= 3) {
      Object.assign(body, {
        transportista: form.transportista,
        fechallegada: form.fechallegada,
      });
    }
    formSucio.current = false; // lo guardado es la verdad: cargar() lo refleja
    return ejecutar("/api/importaciones_guardar", body, "Embarque guardado");
  };

  // ---- packing list ------------------------------------------------------

  const abrirPacking = () => inputPacking.current?.click();

  const seleccionarPacking = async (event) => {
    const archivo = event.target.files?.[0];
    // eslint-disable-next-line no-param-reassign
    event.target.value = ""; // permite volver a elegir el mismo archivo
    if (!archivo) return;
    try {
      const r = await parseExcelPreview(archivo, { maxPreviewRows: 100000 });
      const mapa = mapearEncabezados(r.headers);
      if (!mapa.chasis) {
        showAlert({
          message: "No se encontró la columna de chasis/VIN en el archivo",
          color: "warning",
        });
        return;
      }
      const filas = r.rows.map((fila) => {
        const l = {};
        Object.entries(mapa).forEach(([campo, header]) => {
          l[campo] = fila[header];
        });
        return l;
      });
      const { ok, data } = await post("/api/importaciones_lineas_cargar", {
        id,
        confirmar: false,
        lineas: filas,
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo validar el packing list", color: "error" });
        return;
      }
      setPacking({
        nombre: r.fileName,
        filas,
        mapa,
        validas: data.validas || 0,
        errores: data.errores || [],
        avisos: data.avisos || [],
      });
    } catch (e) {
      showAlert({ message: String(e.message || e), color: "error" });
    }
  };

  const confirmarPacking = async () => {
    if (!packing) return;
    const ok = await ejecutar(
      "/api/importaciones_lineas_cargar",
      { id, confirmar: true, lineas: packing.filas },
      "Chasis cargados"
    );
    if (ok) setPacking(null);
  };

  // ---- resumen de liquidación (espejo del cálculo del server) ------------

  const resumenPagos = useMemo(() => {
    const porTipo = {};
    let total = 0;
    let usdSinTasa = false;
    pagos.forEach((p) => {
      const base = montoEnBase(p.monto, p.moneda, p.tasa);
      if (String(p.moneda).toUpperCase() === "U" && toNum(p.tasa) <= 0) usdSinTasa = true;
      porTipo[p.tipo] = (porTipo[p.tipo] || 0) + base;
      total += base;
    });
    return { porTipo, total, usdSinTasa };
  }, [pagos]);

  const sumaFob = useMemo(() => lineas.reduce((s, l) => s + toNum(l.costofob), 0), [lineas]);

  // Reparto por FOB: con varios modelos cada chasis carga según su valor. Si
  // solo algunos traen FOB el reparto mentiría (el backend lo rechaza); si
  // ninguno lo trae se reparte en partes iguales.
  const sinFob = useMemo(() => lineas.filter((l) => toNum(l.costofob) <= 0), [lineas]);
  const fobParcial = sinFob.length > 0 && sinFob.length < lineas.length;
  const pesoFob = (l) => (sumaFob > 0 ? (toNum(l.costofob) / sumaFob) * 100 : 0);

  const costoEstimado = (l) => {
    if (!lineas.length || resumenPagos.total <= 0) return 0;
    if (sumaFob <= 0) return resumenPagos.total / lineas.length;
    return (resumenPagos.total * toNum(l.costofob)) / sumaFob;
  };

  const liquidar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/importaciones_liquidar", {
        id,
        lineas: lineas.map((l) => ({
          id: l.id,
          precio: toNum(precios[l.id]?.precio),
          mprecio: toNum(precios[l.id]?.mprecio),
        })),
      });
      if (ok) {
        // Los avisos son costos típicos sin registrar (transportista,
        // aduanero…): la liquidación salió sin ellos y se puede re-liquidar.
        const avisos = data.avisos || [];
        showAlert({
          message:
            (data.mensajeok || "Embarque liquidado") +
            (avisos.length ? `. ${avisos.join(". ")}` : ""),
          color: avisos.length ? "warning" : "success",
        });
        cambio();
      } else {
        showAlert({ message: data.Error || "No se pudo liquidar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  // ---- columnas ----------------------------------------------------------

  const columnasLineas = [
    { field: "chasis", headerName: "Chasis", flex: 1.2, minWidth: 150 },
    { field: "marca", headerName: "Marca", flex: 0.8, minWidth: 100 },
    { field: "modelo", headerName: "Modelo", flex: 0.8, minWidth: 100 },
    { field: "color", headerName: "Color", width: 90 },
    { field: "ano", headerName: "Año", width: 70 },
    ...(sinCostos
      ? []
      : [
          {
            field: "costofob",
            headerName: `FOB (${embarque?.moneda === "U" ? "US$" : "RD$"})`,
            width: 110,
            renderCell: (p) => fmtMoney(p.row.costofob),
          },
          {
            field: "costoliquidado",
            headerName: "Costo liquidado",
            width: 130,
            renderCell: (p) =>
              p.row.costoliquidado != null ? fmtMoney(p.row.costoliquidado) : "—",
          },
        ]),
    {
      field: "precio",
      headerName: "Precio",
      width: 110,
      renderCell: (p) => (p.row.precio != null ? fmtMoney(p.row.precio) : "—"),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 110,
      renderCell: (p) => chipEstado(p.row.estado, LINEA_CHIP),
    },
    {
      field: "acciones",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        p.row.estado === "pendiente" && vivo ? (
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              sx={{ ...btnBase, color: FP.color.navy }}
              onClick={() => setDialogLinea({ ...p.row })}
            >
              Editar
            </Button>
            {puedeBorrar && (
              <Button
                size="small"
                sx={{ ...btnBase, color: FP.color.danger }}
                onClick={() => setQuitarLinea(p.row)}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </Button>
            )}
          </Stack>
        ) : null,
    },
  ];

  const columnasPagos = [
    {
      field: "tipo",
      headerName: "Tipo",
      width: 170,
      renderCell: (p) => TIPOS_PAGO.find((t) => t.value === p.row.tipo)?.label || p.row.tipo,
    },
    { field: "beneficiario", headerName: "Beneficiario", flex: 1, minWidth: 140 },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 140 },
    {
      field: "monto",
      headerName: "Monto",
      width: 130,
      renderCell: (p) => `${p.row.moneda === "U" ? "US$" : "RD$"} ${fmtMoney(p.row.monto)}`,
    },
    {
      field: "tasa",
      headerName: "Tasa",
      width: 80,
      renderCell: (p) => (p.row.moneda === "U" ? fmtMoney(p.row.tasa) : "—"),
    },
    { field: "fechaestimada", headerName: "Estimada", width: 100 },
    {
      field: "fechapago",
      headerName: "Pagado",
      width: 110,
      renderCell: (p) =>
        p.row.fechapago ? (
          <Chip
            size="small"
            label={p.row.fechapago}
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Pendiente"
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        vivo ? (
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              sx={{ ...btnBase, color: FP.color.navy }}
              onClick={() => setDialogPago({ ...p.row })}
            >
              Editar
            </Button>
            {puedeBorrar && (
              <Button
                size="small"
                sx={{ ...btnBase, color: FP.color.danger }}
                onClick={() => setQuitarPago(p.row)}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </Button>
            )}
          </Stack>
        ) : null,
    },
  ];

  const columnasDocs = [
    {
      field: "tipo",
      headerName: "Tipo",
      width: 150,
      renderCell: (p) => TIPOS_DOCUMENTO.find((t) => t.value === p.row.tipo)?.label || p.row.tipo,
    },
    { field: "nombre", headerName: "Archivo", flex: 1, minWidth: 200 },
    {
      field: "bytes",
      headerName: "Tamaño",
      width: 100,
      renderCell: (p) => `${(toNum(p.row.bytes) / 1024).toFixed(0)} KB`,
    },
    { field: "fecha", headerName: "Subido", width: 100 },
    { field: "user", headerName: "Por", width: 110 },
    {
      field: "acciones",
      headerName: "",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            sx={{ ...btnBase, color: FP.color.navy }}
            onClick={async () => {
              const { ok, data } = await post("/api/importaciones_documento_descargar", {
                id,
                documento_id: p.row.id,
              });
              if (ok) descargarB64(data.archivo_b64, data.nombre, data.mime);
              else showAlert({ message: data.Error || "No se pudo descargar", color: "error" });
            }}
          >
            <DownloadOutlinedIcon sx={{ fontSize: 17 }} />
          </Button>
          {puedeBorrar && (
            <Button
              size="small"
              sx={{ ...btnBase, color: FP.color.danger }}
              onClick={() => setQuitarDoc(p.row)}
            >
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  // ---- render ------------------------------------------------------------

  if (cargando && !embarque) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: FP.color.surface,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!embarque) return null;

  const anticipoCalc =
    toNum(form.montofob) > 0 && toNum(form.porcentajeanticipo) > 0
      ? (toNum(form.montofob) * toNum(form.porcentajeanticipo)) / 100
      : 0;

  const indiceEstado = ESTADOS_VIVOS.indexOf(embarque.estado);
  // El avance manual solo existe cuando la vía es "estado" (de puerto se sale
  // liquidando y de liquidación recibiendo); el backend re-valida los gates.
  const faltantes = (requisitos?.items || []).filter((r) => r.obligatorio && !r.cumplido);
  const avanceManual = vivo && requisitos?.via === "estado" ? requisitos.destino : null;
  // Puerto y liquidación se cierran desde la pestaña Liquidación (liquidar /
  // recibir), no con un cambio de estado: la cabecera lo dice y lleva allí.
  const accionLiquidacion =
    vivo && !sinCostos && ["liquidar", "recibir"].includes(requisitos?.via) ? requisitos.via : null;
  const irALiquidacion = () => {
    setTab(3);
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const estadoAnterior = vivo && indiceEstado > 0 ? ESTADOS_VIVOS[indiceEstado - 1] : null;
  // Sección del formulario que corresponde a la fase actual (se resalta).
  const seccionActiva =
    { negociacion: 0, fabricacion: 0, transito: 1, puerto: 2, liquidacion: 2 }[embarque.estado] ??
    -1;
  const seccionFase = (n) =>
    seccionActiva === n
      ? { ...sectionBox, border: `2px solid ${FP.color.accentBlue}` }
      : sectionBox;
  const FASES = [...ESTADOS_VIVOS, "cerrado"];
  const idxFase = FASES.indexOf(embarque.estado);
  /** Estilo del chip del pipeline: fase actual en navy, recorridas en su color, futuras apagadas. */
  const sxChipFase = (f, i) => {
    if (i === idxFase) return { bgcolor: "#0B1220", color: "#FFFFFF", fontWeight: 700 };
    if (idxFase >= 0 && i < idxFase) {
      return { bgcolor: ESTADO_CHIP[f].bg, color: ESTADO_CHIP[f].fg, fontWeight: 700 };
    }
    return {
      bgcolor: "#FFFFFF",
      color: FP.color.textMuted,
      border: `1px solid ${FP.color.border}`,
      fontWeight: 600,
    };
  };
  let motivoNoLiquidar = "";
  if (!puedeModificar) motivoNoLiquidar = "Su rol no tiene permiso de liquidar";
  else if (!["puerto", "liquidacion"].includes(embarque.estado)) {
    motivoNoLiquidar = "La liquidación va después de la llegada a puerto";
  } else if (fobParcial) {
    motivoNoLiquidar = `Hay ${sinFob.length} chasis sin FOB: el reparto por modelo no cuadra`;
  }
  // No se llena el paso siguiente sin completar el anterior (espejo de las
  // reglas del backend: FASE_MINIMA_* en importacionesbackend.py).
  const puedeFase2 = vivo && indiceEstado >= 1; // embarque: desde fabricación
  const puedeFase3 = vivo && indiceEstado >= 3; // entrega: desde puerto
  const puedeChasis = vivo && indiceEstado >= 2; // packing list: desde tránsito
  const FASE_MINIMA_PAGO = {
    anticipo: 0,
    saldo: 2,
    flete: 2,
    despacho: 3,
    transporte: 3,
    aduanero: 3,
    otro: 0,
  };
  const tiposPagoDisponibles = TIPOS_PAGO.filter((t) => indiceEstado >= FASE_MINIMA_PAGO[t.value]);
  const candado = (texto) => (
    <Typography
      component="span"
      sx={{
        ml: 1,
        fontSize: "0.7rem",
        fontWeight: 700,
        color: FP.color.textMuted,
        textTransform: "none",
        letterSpacing: 0,
      }}
    >
      {texto}
    </Typography>
  );

  const tabs = [
    { titulo: `Chasis (${lineas.length})`, icono: TwoWheelerOutlinedIcon },
    { titulo: `Pagos (${pagos.length})`, icono: PaidOutlinedIcon },
    { titulo: `Documentos (${documentos.length})`, icono: DescriptionOutlinedIcon },
    ...(sinCostos ? [] : [{ titulo: "Liquidación", icono: CalculateOutlinedIcon }]),
  ];
  const tabActual = Math.min(tab, tabs.length - 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title={`Embarque #${embarque.id}`}
        subtitle={embarque.suplidornombre || "Importación de motocicletas"}
        cta={
          <Stack direction="row" spacing={1} alignItems="center">
            {chipEstado(embarque.estado)}
            {accionLiquidacion && (
              <Button
                startIcon={
                  accionLiquidacion === "liquidar" ? (
                    <CalculateOutlinedIcon />
                  ) : (
                    <WarehouseOutlinedIcon />
                  )
                }
                onClick={irALiquidacion}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                {accionLiquidacion === "liquidar" ? "Liquidar mercancía" : "Recibir en inventario"}
              </Button>
            )}
            {avanceManual && (
              <Tooltip
                title={
                  faltantes.length > 0 ? `Falta: ${faltantes.map((f) => f.texto).join(" · ")}` : ""
                }
              >
                <span>
                  <Button
                    startIcon={<ArrowForwardOutlinedIcon />}
                    onClick={() => setConfirmEstado(avanceManual)}
                    disabled={guardando || faltantes.length > 0}
                    sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                  >
                    {ESTADO_CHIP[avanceManual].label}
                  </Button>
                </span>
              </Tooltip>
            )}
            {vivo && puedeBorrar && (
              <Button
                startIcon={<BlockOutlinedIcon />}
                onClick={() => setConfirmCancelar(true)}
                sx={btnOutline(FP.color.danger)}
              >
                Cancelar
              </Button>
            )}
            {onExit && (
              <Button onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {embarque.estado === "cancelado" && (
          <Box sx={{ ...sectionBox, borderColor: FP.color.danger, bgcolor: "#FFF5F5" }}>
            <Typography sx={{ fontWeight: 700, color: FP.color.danger }}>
              Cancelado: {embarque.razoncancelacion}
            </Typography>
          </Box>
        )}

        {/* Pipeline y requisitos para avanzar */}
        <Box sx={sectionBox}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            spacing={0.6}
            sx={{ mb: vivo && requisitos?.destino ? 2 : 0 }}
          >
            {FASES.map((f, i) => (
              <React.Fragment key={f}>
                {i > 0 && (
                  <Typography sx={{ color: FP.color.textMuted, fontSize: "0.8rem" }}>→</Typography>
                )}
                <Chip size="small" label={ESTADO_CHIP[f].label} sx={sxChipFase(f, i)} />
              </React.Fragment>
            ))}
            {embarque.estado === "cancelado" && chipEstado("cancelado")}
          </Stack>
          {vivo && requisitos?.destino && (
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.74rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: FP.color.textSecondary,
                  mb: 1,
                }}
              >
                Para avanzar a {ESTADO_CHIP[requisitos.destino]?.label || requisitos.destino}
              </Typography>
              {accionLiquidacion && (
                <Typography sx={{ fontSize: "0.83rem", color: FP.color.textSecondary, mb: 1 }}>
                  {accionLiquidacion === "liquidar"
                    ? "Se avanza liquidando la mercancía en la pestaña Liquidación: precios de venta por chasis y reparto del costo."
                    : "Se avanza recibiendo los chasis en inventario desde la pestaña Liquidación."}{" "}
                  <Typography
                    component="span"
                    onClick={irALiquidacion}
                    sx={{
                      fontSize: "inherit",
                      fontWeight: 700,
                      color: FP.color.navy,
                      cursor: "pointer",
                    }}
                  >
                    Ir a Liquidación →
                  </Typography>
                </Typography>
              )}
              <Grid container spacing={0.5}>
                {(requisitos.items || []).map((r) => (
                  <Grid item xs={12} sm={6} key={r.clave}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      {r.cumplido ? (
                        <CheckCircleOutlineIcon sx={{ fontSize: 17, color: "#2E7D32" }} />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{
                            fontSize: 17,
                            color: r.obligatorio ? "#E65100" : FP.color.textMuted,
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: "0.83rem",
                          color: r.cumplido ? FP.color.textSecondary : FP.color.textPrimary,
                        }}
                      >
                        {r.texto}
                        {!r.obligatorio && !r.cumplido ? " (sugerido)" : ""}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              {estadoAnterior && (
                <Button
                  size="small"
                  startIcon={<UndoOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setConfirmEstado(estadoAnterior)}
                  sx={{ ...btnBase, color: FP.color.textMuted, mt: 1 }}
                >
                  Devolver a {ESTADO_CHIP[estadoAnterior].label}
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* 1 · Negociación y fabricación */}
        <Box sx={seccionFase(0)}>
          <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.accentBlue}>
            1 · Negociación y fabricación
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                filterOptions={filtrarSuplidores}
                options={suplidores}
                disabled={!vivo}
                value={suplidores.find((s) => s.id === form.suplidor_id) || null}
                getOptionLabel={(o) => (typeof o === "string" ? o : `${o.id} — ${o.descrip || ""}`)}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) =>
                  editarForm((f) => ({
                    ...f,
                    suplidor_id: v?.id || null,
                    suplidornombre: v?.descrip || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Suplidor"
                    sx={fpInputSx}
                    helperText={
                      suplidores.length === 0
                        ? "Sin suplidores registrados (se administran en Cuentas x Pagar)"
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Proforma No."
                disabled={!vivo}
                value={form.proforma || ""}
                onChange={(e) => editarForm((f) => ({ ...f, proforma: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha proforma"
                InputLabelProps={{ shrink: true }}
                disabled={!vivo}
                value={form.fechaproforma || ""}
                onChange={(e) => editarForm((f) => ({ ...f, fechaproforma: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                label="Moneda"
                disabled={!vivo}
                value={form.moneda || "U"}
                onChange={(e) => editarForm((f) => ({ ...f, moneda: e.target.value }))}
                sx={fpInputSx}
              >
                <MenuItem value="U">USD</MenuItem>
                <MenuItem value="P">RD$</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Tasa"
                disabled={!vivo}
                value={form.tasa ?? ""}
                onChange={(e) => editarForm((f) => ({ ...f, tasa: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            {!sinCostos && (
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Monto FOB"
                  disabled={!vivo}
                  value={form.montofob ?? ""}
                  onChange={(e) => editarForm((f) => ({ ...f, montofob: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="% anticipo"
                disabled={!vivo}
                value={form.porcentajeanticipo ?? ""}
                onChange={(e) => editarForm((f) => ({ ...f, porcentajeanticipo: e.target.value }))}
                sx={fpInputSx}
                helperText={
                  !sinCostos && anticipoCalc > 0
                    ? `Anticipo: ${form.moneda === "U" ? "US$" : "RD$"} ${fmtMoney(anticipoCalc)}`
                    : "Negociado por embarque"
                }
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha anticipo"
                InputLabelProps={{ shrink: true }}
                disabled={!vivo}
                value={form.fechaanticipo || ""}
                onChange={(e) => editarForm((f) => ({ ...f, fechaanticipo: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Días fabricación"
                disabled={!vivo}
                value={form.diasfabricacion ?? ""}
                onChange={(e) => editarForm((f) => ({ ...f, diasfabricacion: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 2 · Embarque y tránsito */}
        <Box sx={seccionFase(1)}>
          <SectionTitle icon={DirectionsBoatOutlinedIcon} color={FP.color.accentBlue}>
            2 · Embarque y tránsito
            {!puedeFase2 && vivo && candado("Se habilita al entrar en fabricación")}
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Autocomplete
                freeSolo
                options={navieras}
                disabled={!puedeFase2}
                value={form.naviera || ""}
                onInputChange={(_, v) => editarForm((f) => ({ ...f, naviera: v }))}
                renderInput={(params) => <TextField {...params} label="Naviera" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha embarque"
                InputLabelProps={{ shrink: true }}
                disabled={!puedeFase2}
                value={form.fechaembarque || ""}
                onChange={(e) => editarForm((f) => ({ ...f, fechaembarque: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="ETA puerto"
                InputLabelProps={{ shrink: true }}
                disabled={!puedeFase2}
                value={form.fechaeta || ""}
                onChange={(e) => editarForm((f) => ({ ...f, fechaeta: e.target.value }))}
                sx={fpInputSx}
                helperText="Saldo: ETA-20 · Manifestación: ETA-5"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="BL"
                disabled={!puedeFase2}
                value={form.bl || ""}
                onChange={(e) => editarForm((f) => ({ ...f, bl: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Contenedor"
                disabled={!puedeFase2}
                value={form.contenedor || ""}
                onChange={(e) => editarForm((f) => ({ ...f, contenedor: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Manifestación (ETA-5)"
                InputLabelProps={{ shrink: true }}
                disabled={!puedeFase2}
                value={form.fechamanifestacion || ""}
                onChange={(e) => editarForm((f) => ({ ...f, fechamanifestacion: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 3 · Puerto y entrega */}
        <Box sx={seccionFase(2)}>
          <SectionTitle icon={WarehouseOutlinedIcon} color={FP.color.accentBlue}>
            3 · Puerto y entrega
            {!puedeFase3 && vivo && candado("Se habilita al llegar a puerto")}
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Autocomplete
                freeSolo
                options={transportistas}
                disabled={!puedeFase3}
                value={form.transportista || ""}
                onInputChange={(_, v) => editarForm((f) => ({ ...f, transportista: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Transportista" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Llegada a puerto"
                InputLabelProps={{ shrink: true }}
                disabled={!puedeFase3}
                value={form.fechallegada || ""}
                onChange={(e) => editarForm((f) => ({ ...f, fechallegada: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Notas"
                disabled={!vivo}
                value={form.notas || ""}
                onChange={(e) => editarForm((f) => ({ ...f, notas: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={guardarCabecera}
                disabled={!vivo || guardando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Pestañas */}
        <Box ref={tabsRef} sx={{ ...sectionBox, mb: 0 }}>
          {sinCostos && (
            <Box
              sx={{
                mb: 1.5,
                px: 2,
                py: 1.2,
                borderRadius: 2,
                bgcolor: "#FFF3E0",
                border: "1px solid #FFB74D",
              }}
            >
              <Typography sx={{ fontSize: "0.84rem", color: "#E65100", fontWeight: 600 }}>
                Su rol tiene el marcador &quot;ocultar costos&quot;: no ve montos de pagos ni la
                pestaña de Liquidación, y no puede liquidar ni recibir la mercancía. Un
                administrador debe quitar ese programa al rol en la pantalla de Roles para
                habilitarlo.
              </Typography>
            </Box>
          )}
          <Tabs
            value={tabActual}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons={false}
            TabIndicatorProps={{ sx: { display: "none" } }}
            sx={{
              mb: 2.4,
              minHeight: 44,
              "& .MuiTabs-flexContainer": { gap: 1, width: "100%" },
              "& .MuiTab-root": { flex: { md: 1 }, maxWidth: "none" },
            }}
          >
            {tabs.map((t) => {
              const Icono = t.icono;
              return (
                <Tab
                  key={t.titulo}
                  icon={<Icono sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label={t.titulo}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    minHeight: 40,
                    px: 2,
                    borderRadius: 999,
                    border: `1px solid ${FP.color.border}`,
                    color: FP.color.textSecondary,
                    bgcolor: "#FFFFFF",
                    "& .MuiSvgIcon-root": { color: "inherit" },
                    "&:hover": { borderColor: "#0B1220", color: "#0B1220" },
                    "&.MuiTab-root.Mui-selected": {
                      bgcolor: "#0B1220",
                      borderColor: "#0B1220",
                      color: "#FFFFFF !important",
                    },
                    "&.MuiTab-root.Mui-selected .MuiSvgIcon-root": { color: "#FFFFFF !important" },
                  }}
                />
              );
            })}
          </Tabs>

          {/* --- Chasis --- */}
          {tabActual === 0 && (
            <Box>
              {vivo && !puedeChasis && (
                <Typography sx={{ mb: 2, fontSize: "0.85rem", color: FP.color.textMuted }}>
                  El packing list llega con el embarque: los chasis se cargan a partir de la fase En
                  tránsito.
                </Typography>
              )}
              {puedeChasis && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <Button
                    startIcon={<UploadFileOutlinedIcon />}
                    onClick={abrirPacking}
                    sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                  >
                    Cargar packing list
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setDialogLinea({})}
                    sx={btnOutline(FP.color.navy)}
                  >
                    Agregar chasis
                  </Button>
                  <Button
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={() =>
                      downloadTemplateXlsx("packing_list_plantilla", [
                        "Chasis",
                        "Marca",
                        "Modelo",
                        "Color",
                        "Año",
                        "Cilindraje",
                        "Máquina",
                        "Costo FOB",
                      ])
                    }
                    sx={btnOutline(FP.color.textSecondary)}
                  >
                    Plantilla
                  </Button>
                  <input
                    ref={inputPacking}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style={{ display: "none" }}
                    onChange={seleccionarPacking}
                  />
                </Stack>
              )}
              <DataGrid
                autoHeight
                rows={lineas}
                columns={columnasLineas}
                loading={cargando}
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={fpDataGridSx}
              />
            </Box>
          )}

          {/* --- Pagos --- */}
          {tabActual === 1 && (
            <Box>
              {vivo && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setDialogPago({
                        tipo: tiposPagoDisponibles[0]?.value || "otro",
                        moneda: embarque.moneda,
                        tasa: embarque.tasa || "",
                      })
                    }
                    sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                  >
                    Registrar pago
                  </Button>
                  <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                    En esta fase: {tiposPagoDisponibles.map((t) => t.label).join(" · ")}
                  </Typography>
                </Stack>
              )}
              <DataGrid
                autoHeight
                rows={pagos}
                columns={
                  sinCostos
                    ? columnasPagos.filter((c) => !["monto", "tasa"].includes(c.field))
                    : columnasPagos
                }
                loading={cargando}
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={fpDataGridSx}
              />
            </Box>
          )}

          {/* --- Documentos --- */}
          {tabActual === 2 && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  startIcon={<UploadFileOutlinedIcon />}
                  onClick={() => setDialogDoc(true)}
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Subir documento
                </Button>
              </Stack>
              <DataGrid
                autoHeight
                rows={documentos}
                columns={columnasDocs}
                loading={cargando}
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={fpDataGridSx}
              />
            </Box>
          )}

          {/* --- Liquidación --- */}
          {!sinCostos && tabActual === 3 && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={5}>
                  <Box sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.8rem",
                        mb: 1,
                        color: FP.color.textSecondary,
                      }}
                    >
                      DESEMBOLSOS EN RD$ (BASE)
                    </Typography>
                    {TIPOS_PAGO.filter((t) => resumenPagos.porTipo[t.value]).map((t) => (
                      <Stack key={t.value} direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: "0.85rem" }}>{t.label}</Typography>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                          {fmtMoney(resumenPagos.porTipo[t.value])}
                        </Typography>
                      </Stack>
                    ))}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${FP.color.border}` }}
                    >
                      <Typography sx={{ fontWeight: 800 }}>Total a prorratear</Typography>
                      <Typography sx={{ fontWeight: 800 }}>
                        {fmtMoney(resumenPagos.total)}
                      </Typography>
                    </Stack>
                    {resumenPagos.usdSinTasa && (
                      <Typography sx={{ color: FP.color.danger, fontSize: "0.78rem", mt: 1 }}>
                        Hay pagos en USD sin tasa: corregirlos antes de liquidar.
                      </Typography>
                    )}
                    {liquidado && embarque.totalliquidado != null && (
                      <Typography
                        sx={{
                          color: FP.color.successDark || "#2E7D32",
                          fontSize: "0.78rem",
                          mt: 1,
                        }}
                      >
                        Liquidado el {embarque.fechaliquidacion} por RD${" "}
                        {fmtMoney(embarque.totalliquidado)}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                    El costo real de cada chasis sale del total desembolsado repartido según su FOB:
                    con varios modelos en el contenedor, cada uno carga en proporción a su valor (el
                    % de la derecha de cada FOB). Los precios de venta y mínimo son obligatorios
                    para poder liquidar; la entrada a inventario usa estos tres valores tal cual.
                  </Typography>
                  {fobParcial && (
                    <Typography
                      sx={{ fontSize: "0.83rem", color: "#C62828", fontWeight: 700, mt: 1 }}
                    >
                      {sinFob.length} chasis sin FOB ({sinFob.map((l) => l.chasis).join(", ")}):
                      ponerles su costo FOB desde la pestaña Chasis (Editar) antes de liquidar.
                    </Typography>
                  )}
                  {!fobParcial && sinFob.length > 0 && lineas.length > 0 && (
                    <Typography
                      sx={{ fontSize: "0.83rem", color: "#E65100", fontWeight: 700, mt: 1 }}
                    >
                      Ningún chasis tiene FOB: el costo se repartirá en partes iguales. Si hay
                      modelos distintos, cargar el FOB de cada uno desde la pestaña Chasis.
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {lineas.map((l) => (
                <Grid
                  container
                  spacing={1}
                  key={l.id}
                  alignItems="center"
                  sx={{ mb: 0.5, borderBottom: `1px solid ${FP.color.border}`, pb: 0.5 }}
                >
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.83rem" }}>
                      {l.chasis}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                      {[l.marca, l.modelo].filter(Boolean).join(" ")}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                      FOB
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.83rem",
                        color: toNum(l.costofob) <= 0 && sumaFob > 0 ? "#C62828" : "inherit",
                      }}
                    >
                      {toNum(l.costofob) <= 0 && sumaFob > 0 ? "Sin FOB" : fmtMoney(l.costofob)}
                      {sumaFob > 0 && toNum(l.costofob) > 0 ? ` · ${pesoFob(l).toFixed(1)}%` : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                      {liquidado ? "Costo liquidado" : "Costo estimado"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.83rem", fontWeight: 700 }}>
                      {fmtMoney(l.costoliquidado != null ? l.costoliquidado : costoEstimado(l))}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Precio venta"
                      disabled={!puedeFase3 || l.estado === "recibido"}
                      value={precios[l.id]?.precio ?? ""}
                      onChange={(e) =>
                        setPrecios((s) => ({
                          ...s,
                          [l.id]: { ...s[l.id], precio: e.target.value },
                        }))
                      }
                      sx={fpInputSx}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Precio mínimo"
                      disabled={!puedeFase3 || l.estado === "recibido"}
                      value={precios[l.id]?.mprecio ?? ""}
                      onChange={(e) =>
                        setPrecios((s) => ({
                          ...s,
                          [l.id]: { ...s[l.id], mprecio: e.target.value },
                        }))
                      }
                      sx={fpInputSx}
                    />
                  </Grid>
                </Grid>
              ))}

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Tooltip title={motivoNoLiquidar}>
                  <span>
                    <Button
                      startIcon={<CalculateOutlinedIcon />}
                      onClick={liquidar}
                      disabled={
                        !vivo ||
                        !puedeModificar ||
                        guardando ||
                        lineas.length === 0 ||
                        resumenPagos.total <= 0 ||
                        fobParcial ||
                        !["puerto", "liquidacion"].includes(embarque.estado)
                      }
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      {liquidado ? "Re-liquidar" : "Liquidar embarque"}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={!puedeRecibir ? "La liquidación es obligatoria antes de recibir" : ""}
                >
                  <span>
                    <Button
                      startIcon={<WarehouseOutlinedIcon />}
                      onClick={() => {
                        setEmpresaRecibir(multiempresa.empresas[0]?.empresa || 1);
                        setConfirmRecibir(true);
                      }}
                      disabled={!vivo || !puedeModificar || !puedeRecibir || guardando}
                      sx={btnSolid("#2E7D32", "#1B5E20")}
                    >
                      Recibir en inventario
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      {/* Avanzar estado */}
      <ConfirmarDialog
        abierto={confirmEstado != null}
        titulo={`Pasar a ${confirmEstado ? ESTADO_CHIP[confirmEstado].label : ""}`}
        mensaje="Al avanzar se estampan las fechas propias de la fase si están vacías (anticipo o llegada). Devolver una fase no borra datos, salvo que se salga de Liquidado: ahí se anula la liquidación y habrá que liquidar de nuevo."
        cargando={guardando}
        onCerrar={() => setConfirmEstado(null)}
        onConfirmar={async () => {
          const ok = await ejecutar(
            "/api/importaciones_estado",
            { id, estado: confirmEstado },
            "Estado actualizado"
          );
          if (ok) setConfirmEstado(null);
        }}
      />

      {/* Cancelar embarque */}
      <ConfirmarDialog
        abierto={confirmCancelar}
        titulo={`Cancelar embarque #${embarque.id}`}
        mensaje="El embarque quedará cancelado (baja lógica) y sus chasis liberados."
        destructivo
        razonObligatoria
        cargando={guardando}
        onCerrar={() => setConfirmCancelar(false)}
        onConfirmar={async (razon) => {
          const ok = await ejecutar(
            "/api/importaciones_cancelar",
            { id, razon },
            "Embarque cancelado"
          );
          if (ok) setConfirmCancelar(false);
        }}
      />

      {/* Recibir en inventario */}
      <ConfirmarDialog
        abierto={confirmRecibir}
        titulo="Recibir en inventario"
        mensaje={`Se crearán ${
          lineas.filter((l) => l.estado === "pendiente").length
        } chasis en el inventario con su costo liquidado y precios, y el embarque quedará cerrado.`}
        cargando={guardando}
        onCerrar={() => setConfirmRecibir(false)}
        onConfirmar={async () => {
          const body = { id };
          if (multiempresa.activo) body.empresa = empresaRecibir;
          const ok = await ejecutar("/api/importaciones_recibir", body, "Chasis recibidos");
          if (ok) setConfirmRecibir(false);
        }}
      >
        {multiempresa.activo && (
          <TextField
            select
            fullWidth
            label="Empresa dueña de los chasis"
            value={empresaRecibir}
            onChange={(e) => setEmpresaRecibir(Number(e.target.value))}
            sx={{ ...fpInputSx, mt: 1.5 }}
          >
            {multiempresa.empresas.map((emp) => (
              <MenuItem key={emp.empresa} value={emp.empresa}>
                {emp.empresa} — {emp.nombre || `Empresa ${emp.empresa}`}
              </MenuItem>
            ))}
          </TextField>
        )}
      </ConfirmarDialog>

      {/* Quitar línea / pago / documento */}
      <ConfirmarDialog
        abierto={quitarLinea != null}
        titulo={`Quitar chasis ${quitarLinea?.chasis || ""}`}
        mensaje="La línea se elimina del embarque (aún no existe en inventario)."
        destructivo
        cargando={guardando}
        onCerrar={() => setQuitarLinea(null)}
        onConfirmar={async () => {
          const ok = await ejecutar(
            "/api/importaciones_linea_quitar",
            { id, linea_id: quitarLinea.id },
            "Línea quitada"
          );
          if (ok) setQuitarLinea(null);
        }}
      />
      <ConfirmarDialog
        abierto={quitarPago != null}
        titulo="Quitar pago"
        mensaje={`Se elimina el pago de ${
          quitarPago ? TIPOS_PAGO.find((t) => t.value === quitarPago.tipo)?.label : ""
        }.`}
        destructivo
        cargando={guardando}
        onCerrar={() => setQuitarPago(null)}
        onConfirmar={async () => {
          const ok = await ejecutar(
            "/api/importaciones_pago_quitar",
            { id, pago_id: quitarPago.id },
            "Pago quitado"
          );
          if (ok) setQuitarPago(null);
        }}
      />
      <ConfirmarDialog
        abierto={quitarDoc != null}
        titulo={`Eliminar ${quitarDoc?.nombre || "documento"}`}
        mensaje="El archivo se elimina definitivamente del expediente."
        destructivo
        cargando={guardando}
        onCerrar={() => setQuitarDoc(null)}
        onConfirmar={async () => {
          const ok = await ejecutar(
            "/api/importaciones_documento_quitar",
            { id, documento_id: quitarDoc.id },
            "Documento eliminado"
          );
          if (ok) setQuitarDoc(null);
        }}
      />

      {/* Alta/edición de línea */}
      <Dialog
        open={dialogLinea != null}
        onClose={() => setDialogLinea(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {dialogLinea?.id ? `Editar ${dialogLinea.chasis}` : "Agregar chasis"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            {[
              ["chasis", "Chasis", 6],
              ["marca", "Marca", 6],
              ["modelo", "Modelo", 6],
              ["color", "Color", 6],
              ["ano", "Año", 4],
              ["cilindraje", "Cilindraje", 4],
              ["maquina", "Máquina", 4],
            ].map(([campo, label, md]) => (
              <Grid item xs={6} md={md} key={campo}>
                <TextField
                  fullWidth
                  label={label}
                  value={dialogLinea?.[campo] ?? ""}
                  onChange={(e) => setDialogLinea((d) => ({ ...d, [campo]: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
            ))}
            {vistaLinea && (
              <Grid item xs={12} sx={{ pt: "4px !important" }}>
                <VistaPreviaMarca
                  vista={vistaLinea}
                  onUsar={(sug) => setDialogLinea((d) => ({ ...d, marca: sug.marca, modelo: sug.modelo }))}
                />
              </Grid>
            )}
            {!sinCostos && (
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  label={`Costo FOB (${embarque.moneda === "U" ? "US$" : "RD$"})`}
                  value={dialogLinea?.costofob ?? ""}
                  onChange={(e) => setDialogLinea((d) => ({ ...d, costofob: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogLinea(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
          <Button
            onClick={async () => {
              const ok = await ejecutar(
                "/api/importaciones_linea_guardar",
                {
                  // el spread trae el id de la LINEA: el del embarque va después
                  ...dialogLinea,
                  id,
                  linea_id: dialogLinea?.id || 0,
                  costofob: toNum(dialogLinea?.costofob),
                },
                "Línea guardada"
              );
              if (ok) setDialogLinea(null);
            }}
            disabled={guardando || !String(dialogLinea?.chasis || "").trim()}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alta/edición de pago */}
      <Dialog open={dialogPago != null} onClose={() => setDialogPago(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {dialogPago?.id ? "Editar pago" : "Registrar pago"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={dialogPago?.tipo || "otro"}
                onChange={(e) => setDialogPago((d) => ({ ...d, tipo: e.target.value }))}
                sx={fpInputSx}
              >
                {/* Cada pago entra en su fase; un tipo viejo (tras retroceder
                    de fase) se conserva para poder editarlo. */}
                {TIPOS_PAGO.filter(
                  (t) => indiceEstado >= FASE_MINIMA_PAGO[t.value] || t.value === dialogPago?.tipo
                ).map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Beneficiario"
                value={dialogPago?.beneficiario ?? ""}
                onChange={(e) => setDialogPago((d) => ({ ...d, beneficiario: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Monto"
                value={dialogPago?.monto ?? ""}
                onChange={(e) => setDialogPago((d) => ({ ...d, monto: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                select
                fullWidth
                label="Moneda"
                value={dialogPago?.moneda || "P"}
                onChange={(e) => setDialogPago((d) => ({ ...d, moneda: e.target.value }))}
                sx={fpInputSx}
              >
                <MenuItem value="P">RD$</MenuItem>
                <MenuItem value="U">USD</MenuItem>
              </TextField>
            </Grid>
            {dialogPago?.moneda === "U" && (
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  label="Tasa del día"
                  value={dialogPago?.tasa ?? ""}
                  onChange={(e) => setDialogPago((d) => ({ ...d, tasa: e.target.value }))}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Fecha estimada"
                InputLabelProps={{ shrink: true }}
                value={aInputDate(dialogPago?.fechaestimada) || dialogPago?.fechaestimada_iso || ""}
                onChange={(e) =>
                  setDialogPago((d) => ({
                    ...d,
                    fechaestimada: "",
                    fechaestimada_iso: e.target.value,
                  }))
                }
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Fecha pagado"
                InputLabelProps={{ shrink: true }}
                value={aInputDate(dialogPago?.fechapago) || dialogPago?.fechapago_iso || ""}
                onChange={(e) =>
                  setDialogPago((d) => ({ ...d, fechapago: "", fechapago_iso: e.target.value }))
                }
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Referencia"
                value={dialogPago?.referencia ?? ""}
                onChange={(e) => setDialogPago((d) => ({ ...d, referencia: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={dialogPago?.descripcion ?? ""}
                onChange={(e) => setDialogPago((d) => ({ ...d, descripcion: e.target.value }))}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogPago(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
          <Button
            onClick={async () => {
              const ok = await ejecutar(
                "/api/importaciones_pago_guardar",
                {
                  id,
                  pago_id: dialogPago?.id || 0,
                  tipo: dialogPago?.tipo,
                  descripcion: dialogPago?.descripcion,
                  beneficiario: dialogPago?.beneficiario,
                  monto: toNum(dialogPago?.monto),
                  moneda: dialogPago?.moneda || "P",
                  tasa: toNum(dialogPago?.tasa),
                  fechaestimada:
                    dialogPago?.fechaestimada_iso || aInputDate(dialogPago?.fechaestimada),
                  fechapago: dialogPago?.fechapago_iso || aInputDate(dialogPago?.fechapago),
                  referencia: dialogPago?.referencia,
                },
                "Pago guardado"
              );
              if (ok) setDialogPago(null);
            }}
            disabled={guardando || toNum(dialogPago?.monto) <= 0}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subir documento */}
      <Dialog open={dialogDoc} onClose={() => setDialogDoc(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Subir documento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={docForm.tipo}
                onChange={(e) => setDocForm((d) => ({ ...d, tipo: e.target.value }))}
                sx={fpInputSx}
              >
                {TIPOS_DOCUMENTO.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button component="label" fullWidth sx={btnOutline(FP.color.navy)}>
                {docForm.nombre || "Seleccionar archivo (máx. 4MB)"}
                <input
                  hidden
                  type="file"
                  onChange={(e) => {
                    const archivo = e.target.files?.[0];
                    e.target.value = "";
                    if (!archivo) return;
                    if (archivo.size > 4 * 1024 * 1024) {
                      showAlert({ message: "El archivo no puede exceder 4MB", color: "warning" });
                      return;
                    }
                    const lector = new FileReader();
                    lector.onload = () => {
                      const b64 = String(lector.result || "").split(",")[1] || "";
                      if (b64)
                        setDocForm((d) => ({
                          ...d,
                          nombre: archivo.name,
                          mime: archivo.type,
                          b64,
                        }));
                    };
                    lector.readAsDataURL(archivo);
                  }}
                />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogDoc(false)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
          <Button
            onClick={async () => {
              const ok = await ejecutar(
                "/api/importaciones_documento_subir",
                {
                  id,
                  tipo: docForm.tipo,
                  nombre: docForm.nombre,
                  mime: docForm.mime,
                  archivo_b64: docForm.b64,
                },
                "Documento subido"
              );
              if (ok) {
                setDialogDoc(false);
                setDocForm({ tipo: "packinglist", nombre: "", mime: "", b64: "" });
              }
            }}
            disabled={guardando || !docForm.b64}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Subir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación del packing list */}
      <Dialog open={packing != null} onClose={() => setPacking(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Packing list: {packing?.nombre}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {packing?.validas || 0} chasis válidos de {packing?.filas?.length || 0} filas.
          </Typography>
          <AvisoMarcasEstandar filas={packing?.filas || []} estandar={estandar} sx={{ mt: 1 }} />
          {(packing?.errores || []).length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.danger, fontSize: "0.85rem" }}>
                Errores (corregir el archivo):
              </Typography>
              {(packing?.errores || []).slice(0, 15).map((e) => (
                <Typography key={e} sx={{ fontSize: "0.78rem", color: FP.color.danger }}>
                  • {e}
                </Typography>
              ))}
              {(packing?.errores || []).length > 15 && (
                <Typography sx={{ fontSize: "0.78rem", color: FP.color.danger }}>
                  … y {(packing?.errores || []).length - 15} más
                </Typography>
              )}
            </Box>
          )}
          {(packing?.avisos || []).length > 0 && (
            <Box>
              <Typography sx={{ fontWeight: 700, color: "#E65100", fontSize: "0.85rem" }}>
                Avisos:
              </Typography>
              {(packing?.avisos || []).slice(0, 10).map((a) => (
                <Typography key={a} sx={{ fontSize: "0.78rem", color: "#E65100" }}>
                  • {a}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPacking(null)} sx={btnOutline(FP.color.textSecondary)}>
            Cerrar
          </Button>
          <Button
            onClick={confirmarPacking}
            disabled={
              guardando || (packing?.errores || []).length > 0 || (packing?.validas || 0) === 0
            }
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Cargar {packing?.validas || 0} chasis
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

Embarque.propTypes = {
  id: PropTypes.number.isRequired,
  onExit: PropTypes.func,
  onChanged: PropTypes.func,
};

Embarque.defaultProps = {
  onExit: null,
  onChanged: null,
};
